import React, { useState } from "react";
import { useApp } from "../context/AppContext";
import { toast } from "sonner";
import { Info, MapPin, ArrowRight, AlertCircle } from "lucide-react";

const BRANDS = ["HP", "Dell", "Lenovo", "Asus", "Acer", "Apple", "Custom"];
const ISSUES = [
  "Slow Laptop / Lag",
  "Broken Screen / No Display",
  "Battery Failure",
  "Dead Motherboard / Water Damage",
  "Windows OS Crash",
  "None of the Above / Other Issue",
];

export const BookingForm = ({ onSubmitted }) => {
  const { session, setRoute, addBooking } = useApp(); 
  const [brand, setBrand] = useState("HP");
  const [customBrand, setCustomBrand] = useState("");
  const [model, setModel] = useState("");
  const [issue, setIssue] = useState(ISSUES[0]);
  const [customIssue, setCustomIssue] = useState("");
  const [address, setAddress] = useState("");
  const [description, setDescription] = useState("");
  const [loading, setLoading] = useState(false); 

  const isOther = issue === "None of the Above / Other Issue";

  // 🚀 ASYNC SUBMIT FUNCTION
  const submit = async (e) => {
    e.preventDefault();
    
    if (!session || session.role !== "customer") {
      toast.error("Please log in to confirm your booking");
      onSubmitted?.({ needAuth: true });
      return;
    }
    if (!model.trim()) return toast.error("Model number is required");
    if (!address.trim()) return toast.error("Service address is required");
    if (isOther && !customIssue.trim())
      return toast.error("Please describe the issue under 'Other'");

    const finalBrand = brand === "Custom" ? customBrand.trim() || "Custom" : brand;

    try {
      setLoading(true);

      // ✅ SIRF CONTEXT KE FUNCTION KO CALL KAREIN. 
      // Firestore mein save karne ka aur random gate OTP banane ka poora kaam yahi karega.
      if (addBooking) {
        const liveBooking = await addBooking({
          brand: finalBrand,
          model: model.trim(),
          issue,
          customIssue: isOther ? customIssue.trim() : "",
          address: address.trim(),
          description: description.trim(),
        });

        if (liveBooking) {
          toast.success(`Booking confirmed & synced to cloud!`);
          setRoute("customer-hub");
          onSubmitted?.({ booking: { id: liveBooking.id } });
        } else {
          toast.error("Something went wrong while booking.");
        }
      }

    } catch (error) {
      console.error("Submit Error:", error);
      toast.error("Server connection failed: " + error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={submit} className="card p-8 md:p-10" data-testid="booking-form">
      <div className="grid md:grid-cols-2 gap-6">
        <div>
          <label className="text-sm font-medium text-[var(--ink-1)] block mb-2">
            Laptop brand
          </label>
          <select
            className="field"
            value={brand}
            onChange={(e) => setBrand(e.target.value)}
            data-testid="booking-brand-select"
          >
            {BRANDS.map((b) => (
              <option key={b} value={b}>
                {b}
              </option>
            ))}
          </select>
          {brand === "Custom" && (
            <input
              className="field mt-3"
              placeholder="Enter brand name…"
              value={customBrand}
              onChange={(e) => setCustomBrand(e.target.value)}
              data-testid="booking-custom-brand-input"
            />
          )}
        </div>

        <div>
          <label className="text-sm font-medium text-[var(--ink-1)] block mb-2">
            Model name / number
          </label>
          <input
            className="field"
            placeholder="e.g. Pavilion x360, ThinkPad T14"
            value={model}
            onChange={(e) => setModel(e.target.value)}
            data-testid="booking-model-input"
          />
        </div>

        <div className="md:col-span-2">
          <label className="text-sm font-medium text-[var(--ink-1)] block mb-2">
            What's the main issue?
          </label>
          <select
            className="field"
            value={issue}
            onChange={(e) => setIssue(e.target.value)}
            data-testid="booking-issue-select"
          >
            {ISSUES.map((i) => (
              <option key={i} value={i}>
                {i}
              </option>
            ))}
          </select>
          {isOther && (
            <div className="mt-3 card-warn p-4">
              <div className="flex items-center gap-2 text-sm font-medium text-[#92400e] mb-2">
                <AlertCircle size={14} />
                Please describe your issue
              </div>
              <input
                className="field"
                placeholder="Describe the issue in a short line…"
                value={customIssue}
                onChange={(e) => setCustomIssue(e.target.value)}
                data-testid="booking-custom-issue-input"
                autoFocus
              />
            </div>
          )}
        </div>

        <div className="md:col-span-2">
          <label className="text-sm font-medium text-[var(--ink-1)] block mb-2">
            Service address
          </label>
          <div className="relative">
            <MapPin size={16} className="absolute left-3 top-3.5 text-[var(--ink-4)]" />
            <input
              className="field pl-10"
              placeholder="Flat, building, street, city, PIN"
              value={address}
              onChange={(e) => setAddress(e.target.value)}
              data-testid="booking-address-input"
            />
          </div>
        </div>

        <div className="md:col-span-2">
          <label className="text-sm font-medium text-[var(--ink-1)] block mb-2">
            Describe the problem in detail{" "}
            <span className="text-[var(--ink-3)] font-normal">(optional)</span>
          </label>
          <textarea
            className="field min-h-[110px] resize-y"
            placeholder="Symptoms, when they started, anything you tried…"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            data-testid="booking-description-textarea"
          />
        </div>
      </div>

      {/* Transparency Panel */}
      <div className="mt-8 card-info p-5 flex gap-4" data-testid="booking-policy-panel">
        <div className="w-10 h-10 rounded-xl bg-white border border-[#bfdbfe] flex items-center justify-center flex-shrink-0">
          <Info size={16} className="text-[#1e40af]" />
        </div>
        <div>
          <div className="text-sm font-semibold text-[#1e3a8a]">
            One-time doorstep visit fee: ₹150
          </div>
          <p className="text-sm text-[#1e3a8a]/80 mt-1 leading-relaxed">
            This fixed baseline charge covers the technician's transit to your location, tools &amp;
            safety kit, panel disassembly, and initial fault diagnosis. It's billed once per visit
            and is independent of any replacement parts you approve.
          </p>
        </div>
      </div>

      <div className="mt-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div className="text-xs text-[var(--ink-3)]">
          You'll receive a 4-digit gate code and live tracking after booking.
        </div>
        <button 
          type="submit" 
          className="btn-primary" 
          disabled={loading} 
          data-testid="booking-submit-button"
        >
          {loading ? "Saving Booking..." : "Book my repair"} <ArrowRight size={16} />
        </button>
      </div>
    </form>
  );
};